import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

/* =============================================================================
 * BookDemo — the "Book a demo" modal.
 *
 * There is deliberately no backend here. The site is a static build on
 * Vercel, and standing up a form endpoint would mean storing visitors'
 * contact details somewhere new. Instead, Send opens the visitor's own mail
 * app with everything pre-filled and addressed to matthew@statme.us: the
 * message genuinely comes FROM them, replies thread normally, and no third
 * party ever holds the data. The trade-off (it needs a configured mail app)
 * is called out under the button, with the address shown as a fallback.
 * ===========================================================================*/

const DEMO_EMAIL = 'matthew@statme.us';
const PLACEHOLDER_MSG = "Hi! I'd like to level up with StatMe.";

export function BookDemo({ open, onClose }) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState(PLACEHOLDER_MSG);
  const firstFieldRef = useRef(null);
  const titleId = 'book-demo-title';

  // Focus the first field on open; close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Freeze the page behind the dialog.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const subject = `StatMe demo request${name ? ` from ${name}` : ''}`;
    const body = [
      message,
      '',
      '---',
      `Name: ${name}`,
      `Reach me at: ${contact}`,
    ].join('\n');
    window.location.href = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        // Close on a backdrop click, not on clicks inside the card.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal__head">
          <h2 className="modal__title" id={titleId}>
            Book a demo
          </h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <p className="modal__sub">
          Tell us who you are and how to reach you, and we&rsquo;ll set up a
          walkthrough for your team.
        </p>

        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="bd-name">Name</label>
            <input
              id="bd-name"
              ref={firstFieldRef}
              type="text"
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Coach Jordan Rivera"
            />
          </div>

          <div className="field">
            <label htmlFor="bd-contact">Email or phone</label>
            <input
              id="bd-contact"
              type="text"
              required
              autoComplete="email"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="you@school.org or (512) 555-0100"
            />
          </div>

          <div className="field">
            <label htmlFor="bd-message">Message</label>
            <textarea
              id="bd-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <button className="btn btn--primary" type="submit" style={{ width: '100%' }}>
            Send
          </button>
        </form>

        <p className="modal__note">
          Send opens your email app with this message addressed to{' '}
          <a href={`mailto:${DEMO_EMAIL}`} style={{ textDecoration: 'underline' }}>
            {DEMO_EMAIL}
          </a>
          . Prefer to write directly? That address reaches us too.
        </p>
      </div>
    </div>
  );
}

export default BookDemo;
